import { Flex, Box, Button, IconButton, Spinner } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { LocationStoreView } from '../../location/types';
import {
    AttributeOption,
    CautionsTabLocation,
    ConsolidatedLocationView,
    EventPanelMetadata,
} from '@mark43/cad-api';
import React, { ReactNode } from 'react';
import { LocationSelectFormattedLocationDisplay } from './common/location-select-formatted-location-display';
import { PrioritizedLocationCautionMarker } from '../../common/components/location-caution-marker';
import { LocationsSection as LocationCautionsSection } from '../../event-panel/tabs/cautions/components/sections/locations-section';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { LocationRiskPriority } from '../../common/components/location-risk-priority';
import { CommonSize, getBasicCommonSize } from '../../common/helpers/utils';
import { HighlightBox } from '../../common/components/highlighted-box';
import { useEventLocationNotifications } from '../../common/hooks/use-event-location-notifications';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';

export const LocationSelectValueDisplay = observer(
    (props: {
        value?: ConsolidatedLocationView | LocationStoreView | null;
        onClick?: () => void;
        onDelete?: () => void;
        hasFormErrors?: boolean;
        cautionProps?: {
            cautions?: CautionsTabLocation;
            areCautionsLoading?: boolean;
            highestPriorityLocationCaution?: EventPanelMetadata['highestPriorityLocationCaution'];
            isNearbyLocationCautionPriority1?: EventPanelMetadata['isNearbyLocationCautionPriority1'];
            onClickCautionMarker?: React.MouseEventHandler;
        };
        locationSelectTriggerRef?: React.MutableRefObject<HTMLDivElement | null>;
        isDisabled?: boolean;
        locationTitle?: string;
        complementaryContent?: ReactNode;
        coordinatesBlockContent?: ReactNode;
        eventPriority?: AttributeOption;
        riskLevel?: AttributeOption;
        size?: CommonSize;
        dataTestId?: string;
    }) => {
        const {
            value,
            cautionProps,
            hasFormErrors,
            isDisabled,
            locationTitle,
            complementaryContent,
            coordinatesBlockContent,
            onDelete,
            dataTestId,
        } = props;
        const { isFirstResponder } = useApplicationType();
        const unitAssignment = useUnitAssignment();
        const eventId = unitAssignment?.assignedEvent?.eventId;
        const { isLocationUpdated } = useEventLocationNotifications(eventId);

        const size = props.size ?? 'sm';

        if (value === null || value === undefined) {
            return (
                <div ref={props.locationSelectTriggerRef}>
                    <Button
                        onClick={props.onClick}
                        onKeyDown={props.onClick}
                        leadingVisual="Add"
                        variant="outline"
                        size={getBasicCommonSize(size) ?? 'lg'}
                        isFullWidth
                        style={{
                            justifyContent: 'left',
                            fontWeight: 'normal',
                            color: 'var(--arc-colors-text-primary)',
                            borderColor: hasFormErrors
                                ? 'var(--arc-colors-negative-default)'
                                : 'var(--arc-colors-border-default)',
                            borderWidth: hasFormErrors ? '2px' : '1px',
                        }}
                        data-testid="LOCATION_SELECT_DISPLAY"
                        isDisabled={isDisabled}
                    >
                        Location
                    </Button>
                </div>
            );
        }

        const isLocationHighlighted = isLocationUpdated && isFirstResponder;

        return (
            <Flex
                justifyContent="space-between"
                alignItems="flex-start"
                w="100%"
                borderRadius={8}
                borderColor="border.default"
                borderWidth="1px"
                p={2}
                onClick={props.onClick}
                ref={props.locationSelectTriggerRef}
                pointerEvents={isDisabled ? 'none' : 'unset'}
                opacity={isDisabled ? '0.4' : undefined}
                userSelect={isDisabled ? 'none' : undefined}
                aria-disabled={isDisabled}
                data-testid={dataTestId}
                {...(props.onClick
                    ? {
                          cursor: 'pointer',
                          _hover: { bg: 'surface.secondary' },
                      }
                    : {})}
            >
                <Box flex={1} noOfLines={1}>
                    <HighlightBox highlight={isLocationHighlighted}>
                        <LocationSelectFormattedLocationDisplay
                            location={value}
                            showVerifiedIndicator
                            showCrossStreets
                            locationTitle={locationTitle}
                            size={size}
                            isHighlighted={isLocationHighlighted}
                        />
                    </HighlightBox>
                    {coordinatesBlockContent}
                    {isFirstResponder && cautionProps && (
                        <Box p={1}>
                            {cautionProps?.areCautionsLoading ? (
                                <Spinner />
                            ) : (
                                <LocationCautionsSection
                                    data={cautionProps.cautions}
                                    isLoading={!!cautionProps?.areCautionsLoading}
                                />
                            )}
                        </Box>
                    )}
                </Box>
                <Flex alignItems="center">
                    <LocationRiskPriority
                        eventPriority={props.eventPriority}
                        riskLevel={props.riskLevel}
                        size={size}
                    />

                    {cautionProps?.highestPriorityLocationCaution ||
                    cautionProps?.isNearbyLocationCautionPriority1 ? (
                        <PrioritizedLocationCautionMarker
                            onClick={(e) => {
                                e.stopPropagation();
                                cautionProps?.onClickCautionMarker?.(e);
                            }}
                            isNearbyLocationCautionPriority1={
                                cautionProps?.isNearbyLocationCautionPriority1
                            }
                            highestPriorityLocationCaution={
                                cautionProps?.highestPriorityLocationCaution
                            }
                            size={getBasicCommonSize(size)}
                        />
                    ) : undefined}
                    {complementaryContent}
                    {!!onDelete && (
                        <IconButton
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            variant="ghost"
                            aria-label="Delete Location"
                            icon="TrashCan"
                            size={size}
                        />
                    )}
                </Flex>
            </Flex>
        );
    }
);
